import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../../config/firebaseConfig";
import "../../../styles/common.css";
import "./TutorConversationsList.css";

const TutorConversationsList = ({ setPageTitle }) => {
  const [conversations, setConversations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [tutors, setTutors] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    setPageTitle("My AI Tutor Conversations");
    fetchConversations();
    fetchTutors();
  }, [setPageTitle]);

  const fetchConversations = async () => {
    setIsLoading(true);
    try {
      const getStudentTutorConversations = httpsCallable(
        functions,
        "getStudentTutorConversations"
      );
      const result = await getStudentTutorConversations();

      if (result.data && result.data.conversations) {
        setConversations(result.data.conversations);
      }
    } catch (error) {
      console.error("Error fetching conversations:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTutors = async () => {
    try {
      const getTutorsFunction = httpsCallable(functions, "getTutors");
      const result = await getTutorsFunction();

      if (result.data && result.data.tutors) {
        const tutorsMap = {};
        result.data.tutors.forEach((tutor) => {
          tutorsMap[tutor.id] = tutor;
        });
        setTutors(tutorsMap);
      }
    } catch (error) {
      console.error("Error fetching tutors:", error);
    }
  };

  const handleConversationClick = (conversation) => {
    navigate(
      `/ai-tutors/${conversation.tutorId}/conversations/${conversation.id}`
    );
  };

  const handleNewConversationClick = (tutorId) => {
    navigate(`/ai-tutors/${tutorId}`);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    if (timestamp._seconds) {
      return new Date(timestamp._seconds * 1000).toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    }
    return new Date(timestamp).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  if (isLoading) {
    return (
      <div className="tconv-loading-container">
        <div className="tconv-loading-ring"></div>
        <p>Loading your conversations...</p>
      </div>
    );
  }

  return (
    <div className="container">
      <h1>My AI Tutor Conversations</h1>

      <div className="tconv-tutor-container">
        <h2>Available Tutors</h2>
        {Object.values(tutors).length > 0 ? (
          <div className="tconv-tutor-cards">
            {Object.values(tutors).map((tutor) => (
              <div key={tutor.id} className="tconv-tutor-card">
                <div className="tconv-tutor-card-content">
                  <h3>{tutor.name}</h3>
                  <p>{tutor.description}</p>
                </div>
                <button
                  className="tconv-new-conversation-button"
                  onClick={() => handleNewConversationClick(tutor.id)}
                >
                  Start New Conversation
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="tconv-no-tutors-message">No tutors available.</p>
        )}
      </div>

      <div className="tconv-conversations-container">
        <h2>Recent Conversations</h2>
        {conversations.length > 0 ? (
          <div className="tconv-conversation-list">
            <table className="table">
              <thead>
                <tr>
                  <th>Tutor</th>
                  <th>Last Message</th>
                  <th>Last Updated</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {conversations.map((conversation) => (
                  <tr
                    key={conversation.id}
                    onClick={() => handleConversationClick(conversation)}
                    className="tconv-conversation-row"
                  >
                    <td>{conversation.tutorName}</td>
                    <td className="tconv-last-message">
                      {conversation.lastMessage || "No messages"}
                    </td>
                    <td>{formatDate(conversation.updatedAt)}</td>
                    <td>{formatDate(conversation.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="tconv-no-conversations-message">
            You haven't had any conversations with AI tutors yet. Choose a tutor
            above to get started!
          </p>
        )}
      </div>
    </div>
  );
};

export default TutorConversationsList;
